import React, { useState, useEffect, useCallback } from "react";
import {
  DollarSign, TrendingUp, Package, Loader2, Calendar,
  ArrowUpRight, ArrowDownRight, Download, BarChart3, RefreshCw
} from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinanceTab = ({ auth, authAxios }) => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("overview");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, txRes] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/finance/summary`),
        authAxios.get(`${API_URL}/api/admin/finance/transactions`),
      ]);
      setSummary(sumRes.data);
      setTransactions(txRes.data);
    } catch (err) {
      logger.error("Finance fetch error:", err);
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (amt) => {
    if (!amt && amt !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amt / 100);
  };

  const exportTransactions = () => {
    if (!transactions.length) return;
    const headers = ["Date", "Booking ID", "Customer", "Package", "Amount", "Payment Status"];
    const rows = transactions.map(t => [
      t.date || "", t.id || "", t.customer_name || "", t.package || "",
      t.final_amount ? (t.final_amount / 100).toFixed(0) : "0", t.payment_status || ""
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `doozygo_transactions_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" size={32} /></div>;
  }

  const monthlyData = summary?.monthly_revenue || {};
  const packageData = summary?.package_revenue || {};
  const sortedMonths = Object.keys(monthlyData).sort().reverse();
  const currentMonth = new Date().toISOString().slice(0, 7);
  const lastMonth = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 7);
  const currentRev = monthlyData[currentMonth] || 0;
  const lastRev = monthlyData[lastMonth] || 0;
  const growth = lastRev > 0 ? ((currentRev - lastRev) / lastRev * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="finance-tab">
      {/* Sub-tab Navigation */}
      <div className="flex gap-2">
        {[
          { key: "overview", label: "Overview", icon: BarChart3 },
          { key: "transactions", label: "Transactions", icon: DollarSign },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${subTab === tab.key ? "bg-emerald-500 text-white shadow-md" : "bg-white text-[#475569] hover:bg-emerald-50"}`}
            data-testid={`finance-subtab-${tab.key}`}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={16} className="text-[#94a3b8]" /></button>
      </div>

      {subTab === "overview" && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-[#94a3b8] font-medium uppercase tracking-wide">Total Revenue</p>
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center"><DollarSign size={16} className="text-emerald-600" /></div>
              </div>
              <p className="text-2xl font-bold text-[#1E1B4B]" data-testid="total-revenue">{formatCurrency(summary?.total_revenue || 0)}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-[#94a3b8] font-medium uppercase tracking-wide">Total Bookings</p>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center"><Calendar size={16} className="text-blue-600" /></div>
              </div>
              <p className="text-2xl font-bold text-[#1E1B4B]" data-testid="total-bookings">{summary?.total_bookings || 0}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-[#94a3b8] font-medium uppercase tracking-wide">This Month</p>
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center"><TrendingUp size={16} className="text-purple-600" /></div>
              </div>
              <p className="text-2xl font-bold text-[#1E1B4B]">{formatCurrency(currentRev)}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-[#94a3b8] font-medium uppercase tracking-wide">MoM Growth</p>
                <div className={`w-8 h-8 ${Number(growth) >= 0 ? "bg-green-100" : "bg-red-100"} rounded-lg flex items-center justify-center`}>
                  {Number(growth) >= 0 ? <ArrowUpRight size={16} className="text-green-600" /> : <ArrowDownRight size={16} className="text-red-600" />}
                </div>
              </div>
              <p className={`text-2xl font-bold ${Number(growth) >= 0 ? "text-green-600" : "text-red-600"}`}>{growth}%</p>
            </div>
          </div>

          {/* Monthly Revenue */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-emerald-500" /> Monthly Revenue</h3>
            {sortedMonths.length === 0 ? (
              <p className="text-sm text-[#94a3b8] text-center py-4">No revenue data yet</p>
            ) : (
              <div className="space-y-3">
                {sortedMonths.slice(0, 12).map(month => {
                  const rev = monthlyData[month];
                  const maxRev = Math.max(...Object.values(monthlyData), 1);
                  const pct = (rev / maxRev * 100).toFixed(0);
                  return (
                    <div key={month} className="flex items-center gap-4">
                      <p className="text-sm font-medium text-[#475569] w-20">{month}</p>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full flex items-center justify-end pr-2 transition-all"
                          style={{ width: `${Math.max(pct, 5)}%` }}
                        >
                          <span className="text-xs text-white font-bold">{formatCurrency(rev)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Package Revenue */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-4 flex items-center gap-2"><Package size={18} className="text-blue-500" /> Revenue by Package</h3>
            {Object.keys(packageData).length === 0 ? (
              <p className="text-sm text-[#94a3b8] text-center py-4">No package data yet</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(packageData).map(([pkg, rev]) => {
                  const colors = {
                    Basic: "from-blue-400 to-blue-600",
                    Classic: "from-purple-400 to-purple-600",
                    Premium: "from-amber-400 to-amber-600",
                    Luxury: "from-pink-400 to-pink-600",
                  };
                  return (
                    <div key={pkg} className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className={`w-12 h-12 bg-gradient-to-br ${colors[pkg] || "from-gray-400 to-gray-600"} rounded-xl flex items-center justify-center text-white font-bold mx-auto mb-2`}>
                        {pkg[0]}
                      </div>
                      <p className="font-bold text-[#1E1B4B]">{formatCurrency(rev)}</p>
                      <p className="text-xs text-[#94a3b8]">{pkg}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {subTab === "transactions" && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b flex items-center justify-between">
            <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2"><DollarSign size={18} className="text-emerald-500" /> All Transactions</h3>
            <button
              onClick={exportTransactions}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600"
              data-testid="export-finance-csv"
            >
              <Download size={16} /> Export CSV
            </button>
          </div>

          {transactions.length === 0 ? (
            <p className="text-sm text-[#94a3b8] text-center py-8">No transactions found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-[#94a3b8]">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-[#94a3b8]">Booking ID</th>
                    <th className="text-left px-4 py-3 font-medium text-[#94a3b8]">Customer</th>
                    <th className="text-left px-4 py-3 font-medium text-[#94a3b8]">Package</th>
                    <th className="text-right px-4 py-3 font-medium text-[#94a3b8]">Amount</th>
                    <th className="text-center px-4 py-3 font-medium text-[#94a3b8]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, i) => (
                    <tr key={tx.id || `tx-${i}`} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 text-[#475569]">{tx.date || "-"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#475569]">{tx.id || "-"}</td>
                      <td className="px-4 py-3 text-[#1E1B4B] font-medium">{tx.customer_name || "-"}</td>
                      <td className="px-4 py-3"><span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium">{tx.package || "-"}</span></td>
                      <td className="px-4 py-3 text-right font-bold text-[#1E1B4B]">{formatCurrency(tx.final_amount || tx.payment_amount || 0)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${tx.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                          {tx.payment_status || "pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinanceTab;
